import { CopyOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import {
  Alert,
  Card,
  Flex,
  Input,
  Steps,
  Select,
  notification,
  Typography,
  Form
} from 'antd';
import { ErrorDisplay } from 'components/ErrorDisplay';
import { BaseButton } from 'components/button/button-base';
import { Subtitle, Title } from 'components/mvp-typography';
import { PrintableRootPage } from 'components/page/page-2';
import { apiClinicOnboardingGenerateToken } from 'lib/api/firebase/apis/apis';
import { useCopyToClipboardMutation } from 'lib/hooks/tanstack/use-CopyToClipboardMutation';
import { resolveError } from 'lib/utils/formatters/error/resolve-error';
import { useState } from 'react';
import { routes } from 'routes';

const { Paragraph } = Typography;

export function GenerateClinicSignupLinkPage() {
  const [link, setGeneratedLink] = useState<string | null>(null);
  const [expiresIn, setExpiresIn] = useState<string>('1 day');
  const copyToClipboard = useCopyToClipboardMutation();

  const generateTokenMutation = useMutation({
    mutationFn: apiClinicOnboardingGenerateToken,
    onSuccess: data => {
      const token = data.data;
      if (token) {
        const registrationPath = routes.onboarding.registration;
        const url = new URL(window.location.origin);
        url.pathname = registrationPath;
        url.searchParams.append('token', token.token);
        setGeneratedLink(url.toString());
      } else {
        notification.error({
          message: 'Failed to generate token: No token received.'
        });
      }
    },
    onError: error => {
      notification.error({
        message: 'Failed to generate clinic signup link.',
        description: resolveError(error).message
      });
    }
  });

  const handleGenerateLink = () => {
    generateTokenMutation.mutate({
      expiresIn: expiresIn
    });
  };

  function copyToClipboardWrapper(link: string) {
    copyToClipboard.mutate(link, {
      onSuccess: () => {
        notification.success({
          message: 'Clinic signup link copied to clipboard!',
          description:
            'Share this link with clinic administrators for self-registration.'
        });
      },
      onError: error => {
        notification.error({
          message: 'Failed to copy link to clipboard.',
          description: resolveError(error).message
        });
      }
    });
  }

  return (
    <PrintableRootPage>
      <Flex vertical gap={16} align="center">
        <Card style={{ width: '100%' }}>
          <Flex vertical gap={16}>
            <Title>Generate Organisation Signup Link</Title>
            <Subtitle>
              Click the button below to generate a unique signup link for a new
              organisation.
            </Subtitle>
            <Flex align="center" justify="center">
              <Steps
                current={link ? 1 : 0}
                direction="horizontal"
                style={{ marginBottom: 24, width: '100%', maxWidth: '800px' }}
                items={[
                  { title: 'Step 1', description: 'Configure Link Expiration' },
                  { title: 'Step 2', description: 'Generate Link' },
                  {
                    title: 'Step 3',
                    description: 'Share Link with Organisation'
                  }
                ]}
              />
            </Flex>
            <Form.Item label="Expiration time">
              <Select
                defaultValue="1 day"
                style={{ width: 120 }}
                onChange={value => setExpiresIn(value)}
                options={[
                  { value: '1 day', label: '1 Day' },
                  { value: '7 days', label: '7 Days' },
                  { value: '30 days', label: '30 Days' }
                ]}
              />
            </Form.Item>
            <Flex gap={16} vertical align="center" justify="center">
              {link && (
                <Flex vertical gap={8} style={{ width: '100%' }}>
                  <Paragraph strong>Generated Link:</Paragraph>
                  <Input
                    style={{ width: 'calc(100% - 32px)' }}
                    value={link}
                    readOnly
                    suffix={
                      <CopyOutlined
                        onClick={() => copyToClipboardWrapper(link)}
                      />
                    }
                  />
                  <Alert
                    message="Link generated successfully!"
                    description="Copy this link and share it with the new client. They can use it to register their organiastion."
                    type="success"
                    showIcon
                  />
                </Flex>
              )}

              <BaseButton
                type="primary"
                onClick={handleGenerateLink}
                loading={generateTokenMutation.isPending}
                style={{ width: 'fit-content' }}
              >
                Generate link
              </BaseButton>
            </Flex>
            {generateTokenMutation.isError && (
              <ErrorDisplay error={resolveError(generateTokenMutation.error)} />
            )}
          </Flex>
        </Card>
      </Flex>
    </PrintableRootPage>
  );
}
